<template>
  <v-card id="account-preferences" class="pl-8 pr-10 pt-8 pb-10 mt-4 mb-5 elevation-0">
    <div class="items-flex">
      <h2 class="mr-3">{{ $t('views.myAccount.languageTitle') }}</h2>
      <LanguageSelector />
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.darkMode.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.darkMode.info`)" />
      <div class="ml-2">
        <ThemeSelector switchSelector :loading="loading" />
      </div>
    </div>

    <v-divider class="mb-4 mt-8 account-preferences-divider" />

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.timezone.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.timezone.info`)" />
      <v-autocomplete
        v-model="myUser.preferences.timezone"
        class="account-preferences-timezone ml-5 my-0 py-0"
        :full-width="false"
        :items="timezoneList"
        :disabled="loading"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-autocomplete>
    </div>

    <div class="items-flex pt-5 mt-2">
      <h2 class="mr-2">{{ $t('views.myAccount.defaultPositionsNeCash.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.defaultPositionsNeCash.info`)" />
      <v-switch
        v-model="myUser.preferences.defaultPositionsNeCash"
        class="ml-5 my-0 py-0"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-switch>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.denseTables.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.denseTables.info`)" />
      <v-switch
        v-model="myUser.preferences.denseTables"
        class="ml-5 my-0 py-0"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-switch>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.defaultItemsPerPage.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.defaultItemsPerPage.info`)" />
      <v-select
        v-model="myUser.preferences.defaultItemsPerPage"
        class="account-preferences-default-items-per-page ml-5 my-0 py-0"
        :items="itemsPerPageList"
        :full-width="false"
        :disabled="loading"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-select>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.defaultShowTypeTableTicker.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.defaultShowTypeTableTicker.info`)" />
      <v-switch
        v-model="myUser.preferences.defaultShowTypeTableTicker"
        class="ml-5 my-0 py-0"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-switch>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.stickyFilters.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.stickyFilters.info`)" />
      <v-switch
        v-model="myUser.preferences.stickyFilters"
        class="ml-5 my-0 py-0"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-switch>
    </div>

    <div class="items-flex pt-5">
      <h2 class="mr-2">{{ $t('views.myAccount.showDistributionLegend.label') }}</h2>
      <InfoIcon :size="15" :text="$t(`views.myAccount.showDistributionLegend.info`)" />
      <v-switch
        v-model="myUser.preferences.showDistributionLegend"
        class="ml-5 my-0 py-0"
        :loading="loading"
        hide-details
        @change="updateUserPreferences"
      ></v-switch>
    </div>

    <AccountOptIn
      :myUser="myUser"
      :loading="loading"
      @updateUserPreferences="updateUserPreferences"
    />
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import moment from 'moment-timezone'
import { pick } from 'lodash'
import { updateUser } from '@/lib/api.js'
import LanguageSelector from '@/views/components/core/LanguageSelector'
import ThemeSelector from '@/views/components/core/ThemeSelector'
import InfoIcon from '@/views/components/common/InfoIcon'
import AccountOptIn from '@/views/components/account/AccountOptIn'

export default {
  components: {
    LanguageSelector,
    ThemeSelector,
    InfoIcon,
    AccountOptIn,
  },
  data: () => {
    return {
      loading: false,
      myUser: null,
      timezoneList: [],
    }
  },
  computed: {
    ...mapState(['user']),
    itemsPerPageList() {
      return [5, 10, 15, 20, 30, 50, 100].map((elem) => {
        return {
          // text: elem === -1 ? this.$t('views.myAccount.defaultItemsPerPage.all') : elem,
          text: elem,
          value: elem,
        }
      })
    },
  },
  watch: {
    user: {
      handler() {
        this.resetUser()
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.loading = true
    this.timezoneList = moment.tz.names().filter((zone) => DateTime.local().setZone(zone).isValid)
    this.loading = false
  },
  methods: {
    ...mapActions({ updateUsersInfo: 'UPDATE_USERS_INFO' }),
    resetUser() {
      this.myUser = JSON.parse(JSON.stringify(this.user))
    },
    async updateUserPreferences() {
      this.loading = true
      try {
        const data = pick(this.myUser, ['preferences'])
        await updateUser({ uid: this.user.uid, data })
        await this.updateUsersInfo()
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        this.resetUser()
        this.loading = false
      }
      this.loading = false
    },
  },
}
</script>

<style lang="scss">
#account-preferences {
  h2 {
    font-size: 15px;
    font-weight: 400;
    color: #636363;
  }

  &.theme--dark {
    h2 {
      color: #a5a5a5;
    }
  }

  .items-flex {
    display: flex;
    align-items: center;
  }
  .account-preferences-timezone {
    max-width: 250px;
  }
  .account-preferences-default-items-per-page {
    max-width: 100px;
  }

  .account-preferences-divider {
    max-width: 400px;
    &.theme--dark {
      border-color: #292929;
    }
  }
}
</style>
